import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Home, LogOut, CheckCircle2, Clock, AlertCircle, ClipboardList,
  User, MessageSquare, Bell, Loader2, Send, X, Building,
  Phone, Mail, Calendar, ChevronRight, RefreshCw, Shield, ArrowLeft
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmployeeDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if admin is viewing this dashboard
  const isAdminView = searchParams.get('admin_view') === 'true' || localStorage.getItem('adminImpersonating') === 'true';
  
  const [employeeData, setEmployeeData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    // If admin is viewing, use impersonated employee data
    if (isAdminView) {
      const impersonatedId = localStorage.getItem('impersonatedEmployeeId');
      const impersonatedName = localStorage.getItem('impersonatedEmployeeName');
      const impersonatedEmail = localStorage.getItem('impersonatedEmployeeEmail');
      const impersonatedDept = localStorage.getItem('impersonatedEmployeeDept');
      
      if (impersonatedId) {
        setEmployeeData({
          id: impersonatedId,
          name: impersonatedName,
          email: impersonatedEmail,
          department: impersonatedDept,
          role: 'Agent'
        });
        fetchDataForEmployee(impersonatedId);
        return;
      }
    }
    
    // Regular employee login check
    const isLoggedIn = localStorage.getItem('employeeLoggedIn');
    if (!isLoggedIn) {
      navigate('/employee/login');
      return;
    }
    
    setEmployeeData({
      id: localStorage.getItem('employeeId'),
      name: localStorage.getItem('employeeName'),
      email: localStorage.getItem('employeeEmail'),
      role: localStorage.getItem('employeeRole'),
      department: localStorage.getItem('employeeDepartment')
    });
    
    fetchData();
  }, [navigate, isAdminView]);

  const fetchDataForEmployee = async (employeeId) => {
    setLoading(true);
    try {
      const tasksRes = await fetch(`${API_URL}/api/tasks`);
      if (tasksRes.ok) {
        const allTasks = await tasksRes.json();
        setTasks(allTasks.filter(t => t.assigned_to === employeeId));
      }
      const convRes = await fetch(`${API_URL}/api/conversations`);
      if (convRes.ok) {
        setConversations(await convRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    const employeeId = localStorage.getItem('employeeId');
    
    try {
      // Fetch tasks assigned to this employee
      const tasksRes = await fetch(`${API_URL}/api/tasks`);
      if (tasksRes.ok) {
        const allTasks = await tasksRes.json();
        setTasks(allTasks.filter(t => t.assigned_to === employeeId));
      }
      
      // Fetch conversations
      const convRes = await fetch(`${API_URL}/api/conversations?participant_id=${employeeId}`);
      if (convRes.ok) {
        setConversations(await convRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('employeeLoggedIn');
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('employeeName');
    localStorage.removeItem('employeeEmail');
    localStorage.removeItem('employeeRole');
    localStorage.removeItem('employeeDepartment');
    localStorage.removeItem('employeeId');
    navigate('/employee/login');
  };

  const handleTaskStatus = async (taskId, status) => {
    try {
      await fetch(`${API_URL}/api/tasks/${taskId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      toast({ title: "Task Updated" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const openConversation = async (conversationId) => {
    try {
      const res = await fetch(`${API_URL}/api/conversations/${conversationId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveConversation(data.conversation);
        setMessages(data.messages);
        
        // Mark as read
        await fetch(`${API_URL}/api/conversations/${conversationId}/read?reader_id=${employeeData.id}`, {
          method: 'PUT'
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/conversations/${activeConversation.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: employeeData.id,
          sender_name: employeeData.name,
          sender_type: 'employee',
          message: newMessage
        })
      });
      
      if (res.ok) {
        setNewMessage('');
        openConversation(activeConversation.id);
      }
    } catch (error) {
      toast({ title: "Failed to send", variant: "destructive" });
    }
    setSendingMessage(false);
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    if (status === 'in_progress') return <Clock className="w-5 h-5 text-amber-500" />;
    return <AlertCircle className="w-5 h-5 text-red-500" />;
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF8C42]" />
      </div>
    );
  }

  const exitAdminView = () => {
    localStorage.removeItem('adminImpersonating');
    localStorage.removeItem('impersonatedEmployeeId');
    localStorage.removeItem('impersonatedEmployeeName');
    localStorage.removeItem('impersonatedEmployeeEmail');
    localStorage.removeItem('impersonatedEmployeeDept');
    window.close();
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Admin View Banner */}
      {isAdminView && (
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5" />
            <span className="font-medium">Admin View - Viewing as: {employeeData?.name}</span>
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded">Full Control Enabled</span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="border-white text-white hover:bg-white hover:text-purple-700"
            onClick={exitAdminView}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Exit Admin View
          </Button>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-[#404040] text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">Harry & Jacobs</h1>
            <span className="text-sm bg-[#FF8C42] px-3 py-1 rounded-full">
              {employeeData?.department === 'architecture' ? 'Architecture' : 'Estate'} Team
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-300">{employeeData?.name}</span>
            {!isAdminView && (
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-none">
            <CardContent className="p-4">
              <AlertCircle className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{pendingTasks.length}</div>
              <div className="text-xs opacity-80">Pending Tasks</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-none">
            <CardContent className="p-4">
              <Clock className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{inProgressTasks.length}</div>
              <div className="text-xs opacity-80">In Progress</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-none">
            <CardContent className="p-4">
              <CheckCircle2 className="w-6 h-6 mb-2 opacity-80" />
              <div className="text-2xl font-bold">{completedTasks.length}</div>
              <div className="text-xs opacity-80">Completed</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white p-2 rounded-xl shadow-sm">
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${
              activeTab === 'tasks' ? 'bg-[#FF8C42] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ClipboardList className="w-4 h-4" /> My Tasks
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${
              activeTab === 'messages' ? 'bg-[#FF8C42] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <MessageSquare className="w-4 h-4" /> Messages
            {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0) > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0)}
              </span>
            )}
          </button>
          <Button onClick={fetchData} variant="ghost" size="sm" className="ml-auto">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-4">
            {tasks.length === 0 ? (
              <Card className="shadow-lg">
                <CardContent className="p-8 text-center text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No tasks assigned to you yet</p>
                </CardContent>
              </Card>
            ) : (
              tasks.map((task) => (
                <Card key={task.id} className={`shadow-lg border-l-4 ${
                  task.status === 'completed' ? 'border-l-green-500' :
                  task.status === 'in_progress' ? 'border-l-amber-500' : 'border-l-red-500'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start gap-3">
                        {getStatusIcon(task.status)}
                        <div>
                          <h3 className="font-bold text-lg">{task.title}</h3>
                          <p className="text-gray-600 mt-1">{task.description}</p>
                          <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full ${
                              task.priority === 'high' ? 'bg-red-100 text-red-700' :
                              task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {task.priority} priority
                            </span>
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" /> Due: {task.due_date}
                              </span>
                            )}
                            <span>From: {task.assigned_by}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {task.status !== 'pending' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleTaskStatus(task.id, 'pending')}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {task.status !== 'in_progress' && (
                          <Button 
                            size="sm" 
                            variant={task.status === 'pending' ? 'default' : 'outline'}
                            className={task.status === 'pending' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                            onClick={() => handleTaskStatus(task.id, 'in_progress')}
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                        )}
                        {task.status !== 'completed' && (
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600"
                            onClick={() => handleTaskStatus(task.id, 'completed')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Conversation List */}
            <Card className="shadow-lg md:col-span-1">
              <CardContent className="p-4">
                <h3 className="font-bold mb-4">Conversations</h3>
                {conversations.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No conversations yet</p>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => openConversation(conv.id)}
                        className={`w-full p-3 rounded-lg text-left transition-colors ${
                          activeConversation?.id === conv.id 
                            ? 'bg-[#FF8C42]/10 border border-[#FF8C42]' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">
                              {conv.participant_names?.filter(n => n !== employeeData?.name).join(', ') || 'Chat'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {conv.last_message || 'No messages'}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="shadow-lg md:col-span-2">
              <CardContent className="p-4 h-[500px] flex flex-col">
                {activeConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="flex justify-between items-center pb-3 border-b mb-3">
                      <div>
                        <h3 className="font-bold">
                          {activeConversation.participant_names?.filter(n => n !== employeeData?.name).join(', ')}
                        </h3>
                        <p className="text-xs text-gray-500">{activeConversation.subject || 'Conversation'}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setActiveConversation(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto space-y-3 mb-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === employeeData?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] p-3 rounded-lg ${
                            msg.sender_id === employeeData?.id 
                              ? 'bg-[#FF8C42] text-white' 
                              : 'bg-gray-100'
                          }`}>
                            <p className="text-xs opacity-70 mb-1">{msg.sender_name}</p>
                            <p className="text-sm">{msg.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        className="flex-1"
                      />
                      <Button 
                        onClick={sendMessage} 
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-[#FF8C42] hover:bg-[#FF6B35]"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a conversation to start chatting</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
